import { apiClient } from '@/shared/api';
import type { TableViewSnapshot } from '@/entities/settings/models/types';
import type {
  SharedPreset,
  SharedPresetCreate,
  SharedPresetListResponse,
  SharedPresetPin,
  SharedPresetRecipientListResponse,
  SharedPresetRecipientParams,
  SharedPresetUpdate,
} from './types';

/**
 * Совместные пресеты, видимые текущему пользователю, по GET /shared-preset.
 * Без имени таблицы сервер отдаёт пресеты всех таблиц и дашбордов.
 */
export const fetchSharedPresets = async <TSettings = TableViewSnapshot>(
  tableName?: string,
): Promise<SharedPresetListResponse<TSettings>> => {
  const { data } = await apiClient.get<SharedPresetListResponse<TSettings>>('/shared-preset', {
    params: { 'table-name': tableName },
  });
  return data;
};

/** Пользователи, с которыми можно поделиться пресетом, по GET /shared-preset/recipients */
export const fetchSharedPresetRecipients = async (
  params: SharedPresetRecipientParams,
): Promise<SharedPresetRecipientListResponse> => {
  const { data } = await apiClient.get<SharedPresetRecipientListResponse>('/shared-preset/recipients', { params });
  return data;
};

/** Создание совместного пресета по POST /shared-preset */
export const createSharedPreset = async <TSettings = TableViewSnapshot>(
  preset: SharedPresetCreate<TSettings>,
): Promise<SharedPreset<TSettings>> => {
  const { data } = await apiClient.post<SharedPreset<TSettings>>('/shared-preset', preset);
  return data;
};

/** Изменение названия, содержимого или получателей по PUT /shared-preset/{id} */
export const updateSharedPreset = async <TSettings = TableViewSnapshot>({
  id,
  ...changes
}: SharedPresetUpdate<TSettings>): Promise<SharedPreset<TSettings>> => {
  const { data } = await apiClient.put<SharedPreset<TSettings>>(`/shared-preset/${id}`, changes);
  return data;
};

/** Закрепление в левом меню текущего пользователя по PUT /shared-preset/{id}/pin */
export const pinSharedPreset = async ({ id, isPinned }: SharedPresetPin): Promise<SharedPreset> => {
  const { data } = await apiClient.put<SharedPreset>(`/shared-preset/${id}/pin`, { isPinned });
  return data;
};

/**
 * Удаление совместного пресета по DELETE /shared-preset/{id}: у владельца — у всех,
 * у получателя — только из его списка.
 */
export const deleteSharedPreset = async (id: number): Promise<void> => {
  await apiClient.delete(`/shared-preset/${id}`);
};
